import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, LocalOffer, Visibility } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import apiService from '../services/apiService';

const PromoCodesPage = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState(null);

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const fetchPromoCodes = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPromoCodes();

      if (response.success) {
        setPromoCodes(response.data);
      } else {
        setError(response.message || 'Failed to load promo codes');
        loadMockData();
      }
    } catch (err) {
      console.error('Promo codes error:', err);
      setError('Failed to load promo codes');
      loadMockData();
    } finally {
      setLoading(false);
    }
  };

  const loadMockData = () => {
    const mockPromos = [
      {
        id: '1',
        code: 'FIRST50',
        description: '50% off on first ride',
        discountType: 'percentage',
        discountValue: 50,
        maxDiscount: 100,
        minRideAmount: 50,
        usageLimit: 1000,
        usedCount: 450,
        validFrom: '2025-01-01',
        validUntil: '2025-12-31',
        isActive: true,
      },
      {
        id: '2',
        code: 'SAVE100',
        description: 'Flat ₹100 off',
        discountType: 'fixed',
        discountValue: 100,
        maxDiscount: null,
        minRideAmount: 200,
        usageLimit: 500,
        usedCount: 320,
        validFrom: '2025-01-01',
        validUntil: '2025-06-30',
        isActive: true,
      },
      {
        id: '3',
        code: 'WEEKEND20',
        description: '20% off on weekends',
        discountType: 'percentage',
        discountValue: 20,
        maxDiscount: 150,
        minRideAmount: 100,
        usageLimit: 2000,
        usedCount: 890,
        validFrom: '2025-01-01',
        validUntil: '2025-12-31',
        isActive: true,
      },
    ];
    setPromoCodes(mockPromos);
  };

  const handleAdd = () => {
    setEditMode(false);
    setSelectedPromo(null);
    setOpenDialog(true);
  };

  const handleEdit = (promo) => {
    setEditMode(true);
    setSelectedPromo(promo);
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) {
      return;
    }

    try {
      const response = await apiService.deletePromoCode(id);

      if (response.success) {
        alert('Promo code deleted successfully');
        fetchPromoCodes();
      } else {
        alert(response.message || 'Failed to delete promo code');
      }
    } catch (err) {
      alert('Failed to delete promo code');
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      let response;
      if (editMode) {
        response = await apiService.updatePromoCode(selectedPromo.id, values);
      } else {
        response = await apiService.addPromoCode(values);
      }

      if (response.success) {
        alert(`Promo code ${editMode ? 'updated' : 'created'} successfully`);
        setOpenDialog(false);
        fetchPromoCodes();
      } else {
        alert(response.message || 'Operation failed');
      }
    } catch (err) {
      alert('Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const validationSchema = Yup.object({
    code: Yup.string().required('Code is required').matches(/^[A-Z0-9]+$/, 'Only uppercase letters and numbers'),
    description: Yup.string().required('Description is required'),
    discountType: Yup.string().required('Discount type is required'),
    discountValue: Yup.number().required('Discount value is required').min(1),
    minRideAmount: Yup.number().required('Minimum ride amount is required').min(0),
    usageLimit: Yup.number().required('Usage limit is required').min(1),
    validFrom: Yup.date().required('Valid from is required'),
    validUntil: Yup.date().required('Valid until is required'),
  });

  const getStatusChip = (isActive) => {
    return isActive ? (
      <Chip label="Active" color="success" size="small" />
    ) : (
      <Chip label="Inactive" color="default" size="small" />
    );
  };

  const columns = [
    {
      field: 'code',
      headerName: 'Code',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.row.code}
          color="primary"
          icon={<LocalOffer />}
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 250,
    },
    {
      field: 'discount',
      headerName: 'Discount',
      width: 150,
      renderCell: (params) => {
        if (params.row.discountType === 'percentage') {
          return `${params.row.discountValue}% off`;
        }
        return `₹${params.row.discountValue} off`;
      },
    },
    {
      field: 'usage',
      headerName: 'Usage',
      width: 130,
      renderCell: (params) => `${params.row.usedCount}/${params.row.usageLimit}`,
    },
    {
      field: 'validUntil',
      headerName: 'Valid Until',
      width: 130,
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => getStatusChip(params.row.isActive),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleEdit(params.row)}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Promo Codes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage promotional codes
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
          Add Promo Code
        </Button>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error} - Showing demo data
        </Alert>
      )}

      <Card>
        <CardContent>
          <DataGrid
            rows={promoCodes}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            autoHeight
            loading={loading}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Add/Edit Promo Code Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? 'Edit' : 'Add'} Promo Code</DialogTitle>
        <Formik
          initialValues={{
            code: selectedPromo?.code || '',
            description: selectedPromo?.description || '',
            discountType: selectedPromo?.discountType || 'percentage',
            discountValue: selectedPromo?.discountValue || 0,
            maxDiscount: selectedPromo?.maxDiscount || '',
            minRideAmount: selectedPromo?.minRideAmount || 0,
            usageLimit: selectedPromo?.usageLimit || 100,
            validFrom: selectedPromo?.validFrom || '',
            validUntil: selectedPromo?.validUntil || '',
            isActive: selectedPromo?.isActive ?? true,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
            <Form>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="code"
                      label="Promo Code"
                      value={values.code}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.code && Boolean(errors.code)}
                      helperText={touched.code && errors.code}
                      placeholder="SAVE50"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Discount Type</InputLabel>
                      <Select
                        name="discountType"
                        value={values.discountType}
                        onChange={handleChange}
                        label="Discount Type"
                      >
                        <MenuItem value="percentage">Percentage</MenuItem>
                        <MenuItem value="fixed">Fixed Amount</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="description"
                      label="Description"
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="discountValue"
                      label={values.discountType === 'percentage' ? 'Discount (%)' : 'Discount (₹)'}
                      type="number"
                      value={values.discountValue}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.discountValue && Boolean(errors.discountValue)}
                      helperText={touched.discountValue && errors.discountValue}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="maxDiscount"
                      label="Max Discount (₹)"
                      type="number"
                      value={values.maxDiscount}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      helperText="Leave empty for no limit"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="minRideAmount"
                      label="Min Ride Amount (₹)"
                      type="number"
                      value={values.minRideAmount}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.minRideAmount && Boolean(errors.minRideAmount)}
                      helperText={touched.minRideAmount && errors.minRideAmount}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="usageLimit"
                      label="Usage Limit"
                      type="number"
                      value={values.usageLimit}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.usageLimit && Boolean(errors.usageLimit)}
                      helperText={touched.usageLimit && errors.usageLimit}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="validFrom"
                      label="Valid From"
                      type="date"
                      value={values.validFrom}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.validFrom && Boolean(errors.validFrom)}
                      helperText={touched.validFrom && errors.validFrom}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="validUntil"
                      label="Valid Until"
                      type="date"
                      value={values.validUntil}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.validUntil && Boolean(errors.validUntil)}
                      helperText={touched.validUntil && errors.validUntil}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                <Button type="submit" variant="contained" disabled={isSubmitting}>
                  {editMode ? 'Update' : 'Create'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </Box>
  );
};

export default PromoCodesPage;
